import { Box } from "@mui/material";
import React from "react";
import ProjectSelector from "../components/project/ProjectSelector";
import { useProject } from "../context/ProjectContext";
import EdiphiDatasourcesSection from "../components/datasources/EdiphiDatasourcesSection";
import TakeOffDatasourcesSection from "../components/takeoff/TakeOffDatasourcesSection";
import { EdiphiDatasourceType, TakeOffSourceType, useDatasources } from "../context/DatasourceContext";
import { DrawerProvider, useDrawerActions } from "../context/DrawerContext";
import TakeOffDatasourceDrawer from "../components/takeoff/TakeOffDatasourceDrawer";
import EdiphiDatasourceDrawer from "../components/datasources/EdiphiDatasourceDrawer";
import { EDIPHI_DATASOURCE_ORDER } from "../components/datasources/meta";
import { QTO_SOURCE_ORDER } from "../../integrations/qto/registry";
import { useQtoSourcesEnabled } from "../hooks/useQtoSources";

const DataPageContent = () => {
  const { selectedProject } = useProject();
  const { hasDatasource } = useDatasources();
  const enabledById = useQtoSourcesEnabled();
  const { openDrawer } = useDrawerActions();

  // The default type is resolved here, on the way in, so it reflects the datasources that
  // exist at this instant — the drawers stay mounted between visits and so have no mount of
  // their own to recompute on.
  const handleAddEdiphiDatasourceClick = () => {
    openDrawer(
      "ediphi",
      "add",
      EDIPHI_DATASOURCE_ORDER.find((type) => !hasDatasource("ediphi", type))
    );
  };

  const handleAddTakeOffSourceClick = () => {
    openDrawer(
      "takeOff",
      "add",
      // Skips flag-disabled types, so Add cannot land on a tile the drawer renders disabled.
      QTO_SOURCE_ORDER.find((type) => enabledById[type] && !hasDatasource("takeOff", type))
    );
  };

  const handleEditDatasourceClick = (datasourceType: EdiphiDatasourceType) => {
    openDrawer("ediphi", "edit", datasourceType);
  };

  const handleEditTakeOffClick = (sourceType: TakeOffSourceType) => {
    openDrawer("takeOff", "edit", sourceType);
  };

  return (
    <>
      {!selectedProject ? (
        <ProjectSelector />
      ) : (
        <>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            sx={{
              py: 2.5,
              gap: 2.5,
              minHeight: "100%",
            }}
          >
            <EdiphiDatasourcesSection
              handleAddClick={handleAddEdiphiDatasourceClick}
              handleEditClick={handleEditDatasourceClick}
            />
            <TakeOffDatasourcesSection
              handleAddClick={handleAddTakeOffSourceClick}
              handleEditClick={handleEditTakeOffClick}
            />
          </Box>
          <EdiphiDatasourceDrawer />
          <TakeOffDatasourceDrawer />
        </>
      )}
    </>
  );
};

/**
 * The provider wraps the page's own tree rather than sitting in App.tsx: App renders one page
 * from a switch, so this page unmounts on tab navigation and the drawer state resets with it.
 * Hoisting the provider would leave a drawer open across a tab round-trip.
 */
const DataPage = () => (
  <DrawerProvider>
    <DataPageContent />
  </DrawerProvider>
);

export default DataPage;
